import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Circle } from '@mui/icons-material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  Card,
  CardContent,
  Box,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Icon,
  Button,
  Checkbox,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import dayjs from 'dayjs';
import {
  collection,
  doc as documentForFirestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { db } from '../../api/firebase/config';
import { companySnapshot } from '../../api/firebase/firestore/company';
import { issueSnapshot } from '../../api/firebase/firestore/issue';
import { mediaSnapshot } from '../../api/firebase/firestore/media';
import {
  negotiationsSnapByAgentId,
  negotiationsSnapByClientId,
  negotiationsSnapByPublisherId
} from '../../api/firebase/firestore/negotiation';
import { userSnapshot } from '../../api/firebase/firestore/user';
import {
  cardBorder,
  black,
  primaryButtonBackgroundColor,
  blueButton,
  blue,
  blackText
} from '../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../constants/Location';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';

import CustomSelectField from './CustomSelectField';
import CustomTextfield from './CustomTextfield';

import type { NegotiationType } from '../../api/firebase/firestore/negotiation/negotiationType';
import type { MenuListProps } from '@mui/material';
import type { Query, Timestamp } from 'firebase/firestore';

const styles = {
  blueButton,
  blueGray: {
    color: '#808A9F',
    fontSize: '16px'
  },
  button: {
    fontSize: '12px',
    height: '24px',
    margin: '4px',
    padding: '0px',
    width: '120px'
  },
  card: {
    '@media (max-width: 960px)': {
      margin: '16px'
    },
    boxShadow: 0,
    margin: '24px',
    paddingBottom: '104px'
  },
  cardBorder,
  cardContent: {
    padding: 0
  },
  cardContentSearch: {
    '@media (max-width: 960px)': {
      margin: '0px'
    },
    margin: '24px 0px 0px 24px'
  },
  circle: { color: '#BD0017', display: 'block', fontSize: '8px' },
  client: { color: black, fontSize: '14px' },
  date: {
    color: '#808A9F'
  },
  guideCard: {
    '@media screen and (max-width:600px)': {
      width: '80%'
    },
    width: '480px'
  },
  guideCloseButton: {
    color: blue
  },
  guideText: {
    color: blackText,
    fontSize: '14px',
    marginBottom: '16px',
    whiteSpace: 'pre-wrap'
  },
  guideTitle: {
    color: blackText,
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '8px',
    padding: 0
  },
  icon: {
    alignItems: 'center',
    display: 'flex',
    height: '32px',
    justifyContent: 'center',
    width: '32px'
  },
  lockIcon: { color: '#808A9F', ml: '-48px', mr: '24px' },
  menuItem: {
    '&:hover': {
      backgroundColor: '#DBDBE0',
      color: black
    },
    backgroundColor: '#F5F5F5'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper,
  primaryButtonBackgroundColor,
  publish: {
    alignItems: 'flex-end',
    color: '#808A9F',
    display: 'flex',
    fontSize: '12px'
  },
  searchBar: {
    '@media (max-width: 960px)': {
      marginLeft: '0px'
    },
    marginLeft: '16px'
  },
  searchButton: {
    '@media (max-width: 960px)': {
      marginBottom: '24px',
      marginLeft: '0px',
      marginRight: '0px'
    },
    marginLeft: '16px',
    marginRight: '16px'
  },
  select: {
    '.MuiSelect-icon': {
      color: '#F5F5F5'
    },
    backgroundColor: black,
    color: '#DBDBE0',
    fontSize: '12px',
    height: '30px',
    width: '160px'
  },
  status: { alignItems: 'center', display: 'flex' },
  table: {
    overflowX: 'scroll'
  },
  tableCell: {
    color: black,
    fontSize: '14px'
  },
  tableCellLeft: {
    fontSize: 'inherit',
    marginLeft: '32px'
  },
  tableCellStatus: {
    color: black,
    fontSize: '12px'
  },
  tableHead: {
    backgroundColor: '#F5F5F5'
  },
  tableHeadHeight: {
    height: '40px',
    padding: '0px 16px'
  },
  tableRow: {
    cursor: 'pointer'
  },
  textfield: {
    '@media (max-width: 960px)': {
      maxWidth: '311px',
      width: '100%'
    }
  }
};
interface Props {
  screen: number;
}
type TypeKeys =
  | 'inquiries'
  | 'like'
  | 'likeApproved'
  | 'proposalPreparation'
  | 'negotiation'
  | 'order'
  | 'orderApproval'
  | 'likeRejected'
  | 'decline'
  | 'preparation'
  | 'posted';

const statusMap: Record<TypeKeys, string> = {
  decline: '辞退',
  inquiries: 'お問い合わせ',
  like: 'いいね受取済',
  likeApproved: 'いいね承認済',
  likeRejected: 'いいね拒否',
  negotiation: '商談中',
  order: '発注',
  orderApproval: '発注承認',
  posted: '掲載済',
  preparation: '掲載準備中',
  proposalPreparation: '提案依頼中'
};

const ButtonCheckbox = (propElms: {
  statusName: string;
  onChange: (name: string, isChecked: boolean) => void;
  isChecked: boolean;
}): React.ReactElement => {
  const { statusName, onChange } = propElms;
  const [isChecked, setIsChecked] = React.useState(propElms.isChecked);

  useEffect(() => {
    setIsChecked(propElms.isChecked);
  }, [propElms.isChecked]);

  const buttonBackground = isChecked
    ? { backgroundColor: `${black}!important`, color: '#DBDBE0' }
    : { backgroundColor: '#DBDBE0!important', color: black };

  const toggleChecked = (): void => {
    const newIsChecked = !isChecked;
    setIsChecked(newIsChecked);
    onChange(statusName, newIsChecked);
  };

  return (
    <Button onClick={toggleChecked} sx={[styles.button, buttonBackground]}>
      <Checkbox
        checked={isChecked}
        color="default"
        inputProps={{ 'aria-label': 'primary checkbox' }}
        style={{ display: 'none' }}
      />
      {statusName}
    </Button>
  );
};

const MessageList = (props: Props): React.ReactElement => {
  const urlParams = new URLSearchParams(window.location.search);
  const typeParam = urlParams.get('type');
  const { t } = useTranslation();

  const agentCheckboxes = [
    {
      statusName: t('message.agentStatus.all')
    },
    {
      statusName: t('message.agentStatus.1')
    },
    {
      statusName: t('message.agentStatus.2')
    },
    {
      statusName: t('message.agentStatus.3')
    },
    {
      statusName: t('message.agentStatus.4')
    },
    {
      statusName: t('message.agentStatus.5')
    },
    {
      statusName: t('message.agentStatus.6')
    },
    {
      statusName: t('message.agentStatus.7')
    },
    {
      statusName: t('message.agentStatus.11')
    },
    {
      statusName: t('message.agentStatus.12')
    },
    {
      statusName: t('message.agentStatus.9')
    },
    {
      statusName: t('message.agentStatus.10')
    }
  ];
  const clientCheckboxes = [
    {
      statusName: t('message.clientStatus.all')
    },
    {
      statusName: t('message.clientStatus.1')
    },
    {
      statusName: t('message.clientStatus.2')
    },
    {
      statusName: t('message.clientStatus.3')
    },
    {
      statusName: t('message.clientStatus.4')
    },
    {
      statusName: t('message.clientStatus.5')
    },
    {
      statusName: t('message.clientStatus.6')
    },
    {
      statusName: t('message.clientStatus.7')
    },
    {
      statusName: t('message.clientStatus.11')
    },
    {
      statusName: t('message.clientStatus.12')
    },
    {
      statusName: t('message.clientStatus.9')
    },
    {
      statusName: t('message.clientStatus.10')
    }
  ];

  const publisherCheckboxes = [
    {
      statusName: t('message.publisherStatus.all')
    },
    {
      statusName: t('message.publisherStatus.1')
    },
    {
      statusName: t('message.publisherStatus.2')
    },
    {
      statusName: t('message.publisherStatus.3')
    },
    {
      statusName: t('message.publisherStatus.4')
    },
    {
      statusName: t('message.publisherStatus.5')
    },
    {
      statusName: t('message.publisherStatus.6')
    },
    {
      statusName: t('message.publisherStatus.7')
    },
    {
      statusName: t('message.publisherStatus.11')
    },
    {
      statusName: t('message.publisherStatus.12')
    },
    {
      statusName: t('message.publisherStatus.9')
    },
    {
      statusName: t('message.publisherStatus.10')
    }
  ];

  // Initial checkbox states
  const initialCheckboxStates = new Map<string, boolean>();
  if (!typeParam) {
    initialCheckboxStates.set(t('message.publisherStatus.all'), true);
    initialCheckboxStates.set(t('message.clientStatus.all'), true);
  }

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type');
  const freeWord = queryParams.get('freeWord');
  const { screen } = props;

  const [checkboxStates, setCheckboxStates] = useState<Map<string, boolean>>(
    (screen === screenType.publisher
      ? publisherCheckboxes
      : clientCheckboxes
    ).reduce<Map<string, boolean>>((map, checkbox) => {
      const statusName = checkbox.statusName;
      if (statusName === t('message.clientStatus.all')) {
        return map.set(statusName, true);
      }
      return map.set(statusName, false);
    }, new Map())
  );

  const handleCheckboxChange = (
    statusName: string,
    isChecked: boolean
  ): void => {
    setCheckboxStates((prevState) => {
      const newCheckboxStates = new Map(prevState);
      newCheckboxStates.set(statusName, isChecked);

      if (statusName === t('message.clientStatus.all') && isChecked) {
        // 全てのチェックボックスをfalseに設定
        for (const [key] of newCheckboxStates.entries()) {
          if (key !== t('message.clientStatus.all')) {
            newCheckboxStates.set(String(key), false);
          }
        }
      } else if (statusName !== t('message.clientStatus.all') && isChecked) {
        // 全て以外のチェックボックスが選択された場合、全てのチェックを外す
        newCheckboxStates.set(t('message.clientStatus.all'), false);
      }

      return newCheckboxStates;
    });
  };

  const initialTag = typeParam
    ? statusMap[typeParam as TypeKeys]
    : screen === screenType.client
    ? t('message.clientStatus.all')
    : t('message.publisherStatus.all');

  const [tag, setTag] = React.useState(initialTag);
  const [table, setTable] = React.useState<
    Array<{
      id: string;
      noticeFlg: boolean;
      status: number;
      title: string;
      orienSheetFlg: boolean;
      media: string;
      client: string;
      representative: string;
      updated: Timestamp | null;
      unauthorizedFlg: boolean;
    }>
  >([]);

  const getStatusNameClient = (status: number): string => {
    switch (status) {
      case 1:
        return t('message.clientStatus.1');
      case 2:
        return t('message.clientStatus.2');
      case 3:
        return t('message.clientStatus.3');
      case 4:
        return t('message.clientStatus.4');
      case 5:
        return t('message.clientStatus.5');
      case 6:
        return t('message.clientStatus.6');
      case 7:
        return t('message.clientStatus.7');
      case 8:
        return t('message.clientStatus.8');
      case 98:
        return t('message.clientStatus.9');
      case 99:
        return t('message.clientStatus.10');
      case 9:
        return t('message.clientStatus.11');
      case 10:
        return t('message.clientStatus.12');
      default:
        return '';
    }
  };

  const getStatusNamePublisher = (status: number): string => {
    switch (status) {
      case 1:
        return t('message.publisherStatus.1');
      case 2:
        return t('message.publisherStatus.2');
      case 3:
        return t('message.publisherStatus.3');
      case 4:
        return t('message.publisherStatus.4');
      case 5:
        return t('message.publisherStatus.5');
      case 6:
        return t('message.publisherStatus.6');
      case 7:
        return t('message.publisherStatus.7');
      case 8:
        return t('message.publisherStatus.8');
      case 98:
        return t('message.publisherStatus.9');
      case 99:
        return t('message.publisherStatus.10');
      case 9:
        return t('message.publisherStatus.11');
      case 10:
        return t('message.publisherStatus.12');
      default:
        return '';
    }
  };

  const getStatusNameAgent = (status: number): string => {
    switch (status) {
      case 1:
        return t('message.agentStatus.1');
      case 2:
        return t('message.agentStatus.2');
      case 3:
        return t('message.agentStatus.3');
      case 4:
        return t('message.agentStatus.4');
      case 5:
        return t('message.agentStatus.5');
      case 6:
        return t('message.agentStatus.6');
      case 7:
        return t('message.agentStatus.7');
      case 8:
        return t('message.agentStatus.8');
      case 98:
        return t('message.agentStatus.9');
      case 99:
        return t('message.agentStatus.10');
      case 9:
        return t('message.agentStatus.11');
      case 10:
        return t('message.agentStatus.12');
      default:
        return '';
    }
  };

  const { currentUser } = useContext(AuthContext);

  const [clientList, setClientList] = useState<
    Array<{ value: string; option: string }>
  >([]);

  const [mediaList, setMediaList] = useState<
    Array<{ value: string; option: string }>
  >([]);

  const init = async (): Promise<void> => {
    const user = await userSnapshot(currentUser?.uid as string);
    let negotiationsSnapshot;
    if (screen === screenType.agent) {
      negotiationsSnapshot = await negotiationsSnapByAgentId(
        user.data()?.companyId as string
      );
    } else if (screen === screenType.client) {
      negotiationsSnapshot = await negotiationsSnapByClientId(
        user.data()?.companyId as string
      );
    } else if (screen === screenType.publisher) {
      negotiationsSnapshot = await negotiationsSnapByPublisherId(
        user.data()?.companyId as string
      );
    }
    if (negotiationsSnapshot) {
      negotiationsSnapshot.docs.map(async (doc) => {
        let title = '';
        let orienFlg = false;
        let mediaName = '';
        let clientName = '';
        let representativeName = '';
        let updated: Timestamp | null = null;
        let status = 0;
        let unauthorizedFlg = false;
        if (doc.data()?.status) {
          status = doc.data()?.status as number;
        }
        if (doc.data()?.issueId) {
          const issue = await issueSnapshot(doc.data()?.issueId as string);
          title = issue.data()?.name as string;
          orienFlg = (
            (issue.data()?.orientationSheetPublished as string[]) ?? []
          ).includes(doc.data()?.publisherId as string);
        }
        if (doc.data()?.mediaId) {
          const media = await mediaSnapshot(doc.data()?.mediaId as string);
          mediaName = media.data()?.name as string;
        }

        if (doc.data()?.clientId) {
          const client = await companySnapshot(doc.data()?.clientId as string);
          clientName = client.data()?.name as string;
        }

        if (screen === screenType.agent) {
          if (doc.data()?.publisherMainUid) {
            const representative = await userSnapshot(
              doc.data()?.publisherMainUid as string
            );
            representativeName = representative.data()?.name as string;
          }
        } else if (screen === screenType.client) {
          if (doc.data()?.publisherMainUid) {
            const representative = await userSnapshot(
              doc.data()?.publisherMainUid as string
            );
            representativeName = representative.data()?.name as string;
          }
        } else if (screen === screenType.publisher) {
          if (doc.data()?.agentMainUid) {
            const representative = await userSnapshot(
              doc.data()?.agentMainUid as string
            );
            representativeName = representative.data()?.name as string;
          } else if (doc.data()?.clientMainUid) {
            const representative = await userSnapshot(
              doc.data()?.clientMainUid as string
            );
            representativeName = representative.data()?.name as string;
          }
        }
        const chatQuery = query(
          collection(
            db,
            'negotiations',
            doc.data()?.negotiationId as string,
            'chats'
          ),
          orderBy('created', 'desc'),
          limit(1)
        );
        const notice = await getNotice(
          chatQuery,
          doc.data() as NegotiationType
        );

        async function fetchUpdated(): Promise<Timestamp | null> {
          return await new Promise((resolve, reject) => {
            const unSub = onSnapshot(
              chatQuery,
              (querySnapshot) => {
                unSub();
                const docs = querySnapshot.docs.map((document) =>
                  document.data()
                );
                if (docs.length > 0) {
                  resolve(docs[0].created as Timestamp);
                } else {
                  resolve(null);
                }
              },
              reject
            );
          });
        }

        updated = await fetchUpdated();

        if (screen === screenType.client) {
          if (doc.data()?.clientUnauthorizedMember) {
            unauthorizedFlg = (
              (doc.data()?.clientUnauthorizedMember as string[]) ?? []
            )?.includes(currentUser?.uid ?? '');
          }
        } else if (screen === screenType.publisher) {
          if (doc.data()?.publisherUnauthorizedMember) {
            unauthorizedFlg = (
              (doc.data()?.publisherUnauthorizedMember as string[]) ?? []
            )?.includes(currentUser?.uid ?? '');
          }
        } else if (screen === screenType.agent) {
          if (doc.data()?.agentUnauthorizedMember) {
            unauthorizedFlg = (
              (doc.data()?.agentUnauthorizedMember as string[]) ?? []
            )?.includes(currentUser?.uid ?? '');
          }
        }

        setTable((prev) => [
          ...prev,
          {
            client: clientName,
            id: doc.data()?.negotiationId as string,
            media: mediaName,
            noticeFlg: notice,
            orienSheetFlg: orienFlg,
            representative: representativeName,
            status,
            title,
            unauthorizedFlg,
            updated
          }
        ]);
      });
    }

    if (screen === screenType.agent) {
      const clientData = await getClientData(user.data()?.companyId as string);
      setClientList(clientData);

      const mediaData = await getMediaData(user.data()?.companyId as string);
      setMediaList(mediaData);
    }

    if (type) {
      const statusName = statusMap[type as TypeKeys];
      handleCheckboxChange(statusName, true);
    }
    if (freeWord) {
      setSearchField(freeWord);
    }
  };

  const getMediaData = async (
    agentId: string
  ): Promise<Array<{ value: string; option: string }>> => {
    const negotiationsRef = collection(db, 'negotiations');
    const agentQuery = query(negotiationsRef, where('agentId', '==', agentId));
    const negotiationSnapshots = await getDocs(agentQuery);

    const mediaIds = new Set<string>(); // stringのSetとして型を宣言します。

    negotiationSnapshots.forEach((doc) => {
      const data = doc.data();
      mediaIds.add(data.mediaId as string);
    });

    const mediaIdsArray = Array.from(mediaIds);

    // Promise.all を使用し mediaId の非同期処理を並列に実行します
    // mediaPromises に非同期処理を列挙していきます
    const mediaPromises = mediaIdsArray.map(async (mediaId) => {
      const mediaRef = collection(db, 'medias');
      const mediaQuery = query(mediaRef, where('mediaId', '==', mediaId));
      const mediaSnapshots = await getDocs(mediaQuery);

      // 一つの mediaId に対する処理なので、forEach ではなく直接添字でアクセスします
      const data = mediaSnapshots.docs[0].data();
      return { option: data.name as string, value: data.name as string };
    });

    // すべての非同期処理が完了するまで待ちます
    const mediaDataArray = await Promise.all(mediaPromises);

    return mediaDataArray;
  };

  async function getClientData(
    agentId: string
  ): Promise<Array<{ value: string; option: string }>> {
    const negotiationsRef = collection(db, 'negotiations');
    const q = query(negotiationsRef, where('agentId', '==', agentId));

    const negotiationsSnapshot = await getDocs(q);
    const negotiationsData = negotiationsSnapshot.docs.map((document) =>
      document.data()
    );

    const companyPromises = negotiationsData.map(
      async (data) =>
        await getDoc(
          documentForFirestore(db, 'companies', data.clientId as string)
        )
    );
    const companyDocs = await Promise.all(companyPromises);

    const result: Array<{ value: string; option: string }> = [];
    const clientIds = new Set();

    companyDocs.forEach((companyDoc, i) => {
      const companyData = companyDoc.data();
      const clientId = negotiationsData[i].clientId as string;
      if (companyData && !clientIds.has(clientId)) {
        result.push({
          option: companyData.name as string,
          value: companyData.name as string
        });
        clientIds.add(clientId);
      }
    });

    return result;
  }

  const getNotice = async (
    chatQuery: Query,
    negotiation: NegotiationType
  ): Promise<boolean> => {
    const userId = currentUser?.uid ?? '';
    return await new Promise((resolve) => {
      let notice = false;
      const unsubscribe = onSnapshot(chatQuery, (snapshot) => {
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const isRead = data?.isRead as string[] | undefined;
          if (!isRead?.includes(userId)) {
            if (
              (screen === screenType.client &&
                (negotiation.clientMainUid === userId ||
                  (negotiation.clientMember as string[])?.includes(userId))) ||
              (screen === screenType.publisher &&
                (negotiation.publisherMainUid === userId ||
                  (negotiation.publisherMember as string[])?.includes(
                    userId
                  ))) ||
              (screen === screenType.agent &&
                (negotiation.agentMainUid === userId ||
                  (negotiation.agentMember as string[])?.includes(userId)))
            ) {
              notice = true;
            }
          }
        });
        resolve(notice);
        unsubscribe();
      });
    });
  };

  useEffect(() => {
    void (async () => {
      await init();
    })();
  }, []);

  const placeholder = (): string => {
    if (screen === screenType.agent)
      return t('message.searchPlaceholderMobile');
    if (screen === screenType.publisher)
      return t('message.searchPlaceholderMobile');
    return window.matchMedia('(max-width: 480px)').matches
      ? t('message.searchPlaceholderMobile')
      : t('message.searchPlaceholder');
  };

  const title = (): string =>
    screen === screenType.publisher || screen === screenType.agent
      ? t('message.issueTitlePublisher')
      : t('message.issueTitle');

  const orienOrClient = (): string =>
    screen === screenType.publisher || screen === screenType.agent
      ? t('message.client')
      : t('message.orien');

  const menuListProps: MenuListProps = {
    disablePadding: true
  };

  const noCheckboxChecked = (): boolean => {
    if (window.matchMedia('(max-width: 480px)').matches) {
      return false;
    }
    let noCheckboxCheckedFlg = true;

    checkboxStates.forEach((checked) => {
      if (checked) {
        noCheckboxCheckedFlg = false;
      }
    });

    return noCheckboxCheckedFlg;
  };

  const noSelectedTag = (): boolean => {
    if (window.matchMedia('(min-width: 481px)').matches) {
      return false;
    }
    return tag === '';
  };

  const [searchField, setSearchField] = React.useState('');
  const [searchClient, setSearchClient] = React.useState<number | string>('');
  const [searchMedia, setSearchMedia] = React.useState<number | string>('');

  const searchFieldFlg = (tab: {
    id: string;
    noticeFlg: boolean;
    status: number;
    title: string;
    orienSheetFlg: boolean;
    media: string;
    client: string;
    representative: string;
    updated: Timestamp | null;
    unauthorizedFlg: boolean;
  }): boolean =>
    !searchField ||
    tab.title.includes(searchField) ||
    tab.media.includes(searchField) ||
    tab.client.includes(searchField) ||
    tab.representative.includes(searchField);

  const filteredTable = table
    .filter((tab) => {
      const statusName =
        screen === screenType.client
          ? getStatusNameClient(tab.status)
          : screen === screenType.publisher
          ? getStatusNamePublisher(tab.status)
          : getStatusNameAgent(tab.status);

      // If the all checkbox is set to true and the searchField is not empty, return based on the searchField
      if (
        window.matchMedia('(min-width: 481px)').matches &&
        (checkboxStates.get(t('message.clientStatus.all')) ??
          checkboxStates.get(t('message.publisherStatus.all')) ??
          checkboxStates.get(t('message.agentStatus.all'))) &&
        searchField
      ) {
        return searchFieldFlg(tab);
      } else if (
        window.matchMedia('(min-width: 481px)').matches &&
        (checkboxStates.get(t('message.clientStatus.all')) ??
          checkboxStates.get(t('message.publisherStatus.all')) ??
          checkboxStates.get(t('message.agentStatus.all')))
      ) {
        // If the all checkbox is set to true, return true to display all
        return true;
        // If no filter is applied, don't show any record
      } else if (
        window.matchMedia('(max-width: 480px)').matches &&
        (tag === t('message.clientStatus.all') ??
          tag === t('message.publisherStatus.all') ??
          tag === t('message.agentStatus.all')) &&
        searchField
      ) {
        return searchFieldFlg(tab);
      } else if (
        window.matchMedia('(max-width: 480px)').matches &&
        (tag === t('message.clientStatus.all') ??
          tag === t('message.publisherStatus.all') ??
          tag === t('message.agentStatus.all'))
      ) {
        return true;
      } else if (
        window.matchMedia('(max-width: 480px)').matches &&
        tag === statusName
      ) {
        return searchFieldFlg(tab);
      } else if (!searchField && noCheckboxChecked() && noSelectedTag()) {
        return false;
      }
      // Filter on the basis of checkbox and tag
      if (
        (checkboxStates.get(statusName) ?? tag === statusName) &&
        searchFieldFlg(tab)
      ) {
        return true;
      }

      // Fallback condition to not display any record
      return false;
    })
    .sort(
      (a, b) =>
        (b.updated ? b.updated.seconds : Date.now()) -
        (a.updated ? a.updated.seconds : Date.now())
    )
    .filter((tab) => {
      if (
        (searchClient === '' && searchMedia === '') ||
        (searchClient === 0 && searchMedia === 0) ||
        (searchClient === 0 && searchMedia === '') ||
        (searchClient === '' && searchMedia === 0)
      ) {
        return !tab.unauthorizedFlg;
      } else if (searchClient === '' || searchClient === 0) {
        return !tab.unauthorizedFlg && tab.media === searchMedia;
      } else if (searchMedia === '' || searchMedia === 0) {
        return !tab.unauthorizedFlg && tab.client === searchClient;
      }
      return (
        !tab.unauthorizedFlg &&
        tab.client === searchClient &&
        tab.media === searchMedia
      );
    });

  const navigate = useNavigate();

  const [openGuide, setOpenGuide] = useState(false);

  return (
    <Card sx={[styles.card, styles.cardBorder]}>
      <CardContent sx={styles.cardContentSearch}>
        <Box>
          {screen === screenType.agent ? (
            <Grid container>
              <Grid item xs={12} sm={12} md={3}>
                <CustomTextfield
                  placeholder={placeholder()}
                  value={searchField}
                  onChange={(event): void => setSearchField(event.target.value)}
                  isRequired={false}
                  width={'contactDrawer'}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Typography sx={styles.searchBar}>
                  <CustomSelectField
                    blankText={t('message.select.client')}
                    isRequired={false}
                    defaultValue={0}
                    list={clientList}
                    onChange={(event): void =>
                      setSearchClient(event.target.value as string)
                    }
                    width="search"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Typography sx={styles.searchBar}>
                  <CustomSelectField
                    blankText={t('message.select.media')}
                    isRequired={false}
                    defaultValue={0}
                    list={mediaList}
                    onChange={(event): void =>
                      setSearchMedia(event.target.value as string)
                    }
                    width="search"
                  />
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <CustomTextfield
              placeholder={placeholder()}
              value={searchField}
              onChange={(event): void => setSearchField(event.target.value)}
              isRequired={false}
              width={'message'}
            />
          )}
        </Box>
        {window.matchMedia('(min-width: 481px)').matches &&
          screen === screenType.agent && (
            <Box display="flex" alignItems="center" sx={{ mb: '24px' }}>
              <Typography
                sx={{
                  ...styles.blueGray,
                  alignItems: 'center',
                  display: 'flex'
                }}
              >
                <LockIcon />
                {t('message.orienUnpublish')}
              </Typography>
              <Typography
                sx={{
                  ...styles.blueGray,
                  alignItems: 'center',
                  display: 'flex',
                  ml: '24px'
                }}
              >
                <LockOpenIcon />
                {t('message.orienPublish')}
              </Typography>
            </Box>
          )}
        <Box display="flex" flexDirection="row" flexWrap="wrap">
          {window.matchMedia('(max-width: 480px)').matches &&
          screen === screenType.agent ? (
            <Select
              value={tag}
              onChange={(event): void => setTag(event.target.value)}
              sx={styles.select}
              MenuProps={{
                MenuListProps: menuListProps
              }}
            >
              {agentCheckboxes.map((ele, i) => (
                <MenuItem sx={styles.menuItem} key={i} value={ele.statusName}>
                  {ele.statusName}
                </MenuItem>
              ))}
            </Select>
          ) : window.matchMedia('(max-width: 480px)').matches &&
            screen === screenType.client ? (
            <Select
              value={tag}
              onChange={(event): void => setTag(event.target.value)}
              sx={styles.select}
              MenuProps={{
                MenuListProps: menuListProps
              }}
            >
              {clientCheckboxes.map((ele, i) => (
                <MenuItem sx={styles.menuItem} key={i} value={ele.statusName}>
                  {ele.statusName}
                </MenuItem>
              ))}
            </Select>
          ) : window.matchMedia('(max-width: 480px)').matches &&
            screen === screenType.publisher ? (
            <Select
              value={tag}
              onChange={(event): void => setTag(event.target.value)}
              sx={styles.select}
              MenuProps={{
                MenuListProps: menuListProps
              }}
            >
              {publisherCheckboxes.map((ele, i) => (
                <MenuItem sx={styles.menuItem} key={i} value={ele.statusName}>
                  {ele.statusName}
                </MenuItem>
              ))}
            </Select>
          ) : window.matchMedia('(max-width: 480px)').matches &&
            screen === screenType.client ? (
            <Select
              value={tag}
              onChange={(event): void => setTag(event.target.value)}
              sx={styles.select}
              MenuProps={{
                MenuListProps: menuListProps
              }}
            >
              {clientCheckboxes.map((ele, i) => (
                <MenuItem sx={styles.menuItem} key={i} value={ele.statusName}>
                  {ele.statusName}
                </MenuItem>
              ))}
            </Select>
          ) : window.matchMedia('(max-width: 480px)').matches &&
            screen === screenType.publisher ? (
            <Select
              value={tag}
              onChange={(event): void => setTag(event.target.value)}
              sx={styles.select}
              MenuProps={{
                MenuListProps: menuListProps
              }}
            >
              {publisherCheckboxes.map((ele, i) => (
                <MenuItem sx={styles.menuItem} key={i} value={ele.statusName}>
                  {ele.statusName}
                </MenuItem>
              ))}
            </Select>
          ) : screen === screenType.agent ? (
            clientCheckboxes.map((ele, i) => (
              <ButtonCheckbox
                key={i}
                statusName={ele.statusName}
                isChecked={checkboxStates.get(ele.statusName) ?? false}
                onChange={handleCheckboxChange}
              />
            ))
          ) : screen === screenType.client ? (
            clientCheckboxes.map((ele, i) => (
              <ButtonCheckbox
                key={i}
                statusName={ele.statusName}
                isChecked={checkboxStates.get(ele.statusName) ?? false}
                onChange={handleCheckboxChange}
              />
            ))
          ) : screen === screenType.publisher ? (
            publisherCheckboxes.map((ele, i) => (
              <ButtonCheckbox
                key={i}
                statusName={ele.statusName}
                isChecked={checkboxStates.get(ele.statusName) ?? false}
                onChange={handleCheckboxChange}
              />
            ))
          ) : (
            <></>
          )}
          <Typography
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <HelpOutlineOutlinedIcon
              sx={{ cursor: 'pointer', ml: '4px' }}
              onClick={(e) => {
                e.stopPropagation();
                setOpenGuide(true);
              }}
            />
          </Typography>
          <Dialog open={openGuide} onClose={() => setOpenGuide(false)}>
            <DialogContent sx={styles.guideCard}>
              {(screen === screenType.agent ||
                screen === screenType.client) && (
                <>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.1')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description1')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.2')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description2')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.3')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description3')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.4')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description4')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.5')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description5')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.6')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description6')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.7')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description7')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.11')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description11')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.12')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description12')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.9')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description9')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.clientStatus.10')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.clientStatus.description10')}
                  </DialogContentText>
                </>
              )}
              {screen === screenType.publisher && (
                <>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.1')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description1')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.2')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description2')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.3')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description3')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.4')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description4')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.5')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description5')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.6')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description6')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.7')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description7')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.11')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description11')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.12')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description12')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.9')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description9')}
                  </DialogContentText>
                  <DialogTitle sx={styles.guideTitle}>
                    {t('message.publisherStatus.10')}
                  </DialogTitle>
                  <DialogContentText sx={styles.guideText}>
                    {t('message.publisherStatus.description10')}
                  </DialogContentText>
                </>
              )}
              <DialogActions>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenGuide(false);
                  }}
                  sx={styles.guideCloseButton}
                >
                  {t('form.guideClose')}
                </Button>
              </DialogActions>
            </DialogContent>
          </Dialog>
        </Box>
      </CardContent>
      <Box sx={{ mt: '8px' }}>
        <CardContent sx={[styles.cardContent, styles.table]}>
          <Table sx={{ minWidth: '1136px' }}>
            <TableHead sx={styles.tableHead}>
              <TableRow>
                <TableCell sx={[styles.tableCell, styles.tableHeadHeight]}>
                  <Typography sx={styles.tableCellLeft}>
                    {t('message.status')}
                  </Typography>
                </TableCell>
                <TableCell sx={[styles.tableCell, styles.tableHeadHeight]}>
                  {title()}
                </TableCell>
                <TableCell sx={[styles.tableCell, styles.tableHeadHeight]}>
                  {orienOrClient()}
                </TableCell>
                <TableCell sx={[styles.tableCell, styles.tableHeadHeight]}>
                  {t('message.media')}
                </TableCell>
                <TableCell sx={[styles.tableCell, styles.tableHeadHeight]}>
                  {t('message.representative')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTable.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={(): void => {
                    screen === screenType.client
                      ? navigate(`/client/mypage/message/${row.id}`)
                      : screen === screenType.publisher
                      ? navigate(`/publisher/mypage/message/${row.id}`)
                      : navigate(`/agent/mypage/message/${row.id}`);
                  }}
                  sx={styles.tableRow}
                >
                  <TableCell component="th" scope="row">
                    <Typography sx={styles.status}>
                      {row.noticeFlg ? (
                        <Icon sx={styles.icon}>
                          <Circle sx={styles.circle} />
                        </Icon>
                      ) : (
                        <Icon sx={styles.icon}></Icon>
                      )}
                      <Typography variant="body1" sx={styles.tableCellStatus}>
                        {screen === screenType.client
                          ? getStatusNameClient(row.status)
                          : screen === screenType.publisher
                          ? getStatusNamePublisher(row.status)
                          : getStatusNameAgent(row.status)}
                      </Typography>
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={[
                      styles.tableCell,
                      screen !== screenType.client && { pr: '48px' },
                      { maxWidth: '400px' }
                    ]}
                  >
                    {row.title}
                  </TableCell>
                  <TableCell>
                    <Typography sx={styles.publish}>
                      {screen === screenType.client &&
                        (row.orienSheetFlg ? (
                          <>
                            <LockOpenIcon />
                            {t('message.publish')}
                          </>
                        ) : (
                          <>
                            <LockIcon />
                            {t('message.unpublish')}
                          </>
                        ))}
                      {screen === screenType.publisher &&
                        (row.orienSheetFlg ? (
                          <>
                            <LockOpenIcon sx={styles.lockIcon} />
                            <Typography sx={styles.client}>
                              {row.client}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <LockIcon sx={styles.lockIcon} />
                            <Typography sx={styles.client}>
                              {row.client}
                            </Typography>
                          </>
                        ))}
                      {screen === screenType.agent &&
                        (row.orienSheetFlg ? (
                          <>
                            <LockOpenIcon sx={styles.lockIcon} />
                            <Typography sx={styles.client}>
                              {row.client}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <LockIcon sx={styles.lockIcon} />
                            <Typography sx={styles.client}>
                              {row.client}
                            </Typography>
                          </>
                        ))}
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.tableCell}>{row.media}</TableCell>
                  <TableCell sx={styles.tableCell}>
                    {row.representative}
                  </TableCell>
                  <TableCell sx={[styles.tableCell, styles.date]}>
                    {row.updated &&
                      dayjs(row.updated.toDate()).format('YYYY/MM/DD HH:mm')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredTable.length === 0 && (
            <Typography sx={{ textAlign: 'center', width: '100%' }}>
              {(screen === screenType.client ||
                screen === screenType.agent) && (
                <Typography>
                  <Typography sx={{ mt: '80px' }}>
                    <ChatBubbleOutlineIcon
                      sx={{ color: black, fontSize: '56px' }}
                    />
                  </Typography>
                  <Typography
                    sx={{ color: black, fontSize: '20px', mt: '24px' }}
                  >
                    {t('message.empty.client.1')}
                  </Typography>
                  <Typography sx={{ color: black, fontSize: '20px' }}>
                    {t('message.empty.client.2')}
                  </Typography>
                  <Typography sx={{ mt: '80px' }}>
                    <Button
                      sx={[
                        styles.primaryButtonBackgroundColor,
                        { maxWidth: '400px', width: '80%' }
                      ]}
                      onClick={(): void => {
                        if (screen === screenType.client) {
                          navigate('/client/mypage/media');
                        } else if (screen === screenType.agent) {
                          navigate('/agent/mypage/media');
                        }
                      }}
                    >
                      <Typography>
                        {t('message.empty.client.search')}
                      </Typography>
                    </Button>
                  </Typography>
                </Typography>
              )}
              {screen === screenType.publisher && (
                <Typography>
                  <Typography sx={{ mt: '80px' }}>
                    <ChatBubbleOutlineIcon
                      sx={{ color: black, fontSize: '56px' }}
                    />
                  </Typography>
                  <Typography
                    sx={{ color: black, fontSize: '20px', mt: '24px' }}
                  >
                    {t('message.empty.publisher.1')}
                  </Typography>
                  <Typography sx={{ color: black, fontSize: '20px' }}>
                    {t('message.empty.publisher.2')}
                  </Typography>
                  <Typography sx={{ mt: '80px' }}>
                    <Button
                      sx={[
                        styles.primaryButtonBackgroundColor,
                        { maxWidth: '400px', width: '80%' }
                      ]}
                      onClick={(): void => {
                        navigate('/publisher/mypage/issue');
                      }}
                    >
                      <Typography>
                        {t('message.empty.publisher.search')}
                      </Typography>
                    </Button>
                  </Typography>
                </Typography>
              )}
            </Typography>
          )}
        </CardContent>
      </Box>
    </Card>
  );
};
export default MessageList;
